import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";

// The month grid is Monday first, matching getCalendarCells in EventCalendar.
export const WeekStartsOnMonday = { weekStartsOn: 1 };

// The anchor day travels in the URL as a plain calendar date so shared links stay readable.
const AnchorFormat = "yyyy-MM-dd";

export const CalendarViews = ["month", "year", "agenda"];

export const DefaultCalendarView = "month";

// Falls back to the month when the URL asks for a view this calendar cannot render.
export function toCalendarView(view) {
  return CalendarViews.includes(view) ? view : DefaultCalendarView;
}

// Reads the anchor day from the URL, falling back to today when it is absent or malformed.
export function parseAnchorDate(timeline) {
  if (!timeline) {
    return startOfDay(new Date());
  }

  const parsed = parse(timeline, AnchorFormat, new Date());
  return isValid(parsed) ? startOfDay(parsed) : startOfDay(new Date());
}

export function formatAnchorDate(date) {
  return format(date, AnchorFormat);
}

// Returns the days the month grid actually renders, which includes the leading and
// trailing days borrowed from the neighbouring months to complete whole weeks.
export function getMonthGridBounds(anchorDate) {
  return {
    gridStart: startOfWeek(startOfMonth(anchorDate), WeekStartsOnMonday),
    gridEnd: endOfWeek(endOfMonth(anchorDate), WeekStartsOnMonday),
  };
}

// Returns the half open [startAt, endAt) span the given view renders for the anchor day.
// Boundaries sit on local midnight, which keeps them aligned with the local time the
// events are rendered in, so a day near a boundary lands in the cell the reader sees.
export function getVisibleRange(view, anchorDate) {
  let start;
  let end;

  switch (toCalendarView(view)) {
    case "year":
      start = startOfYear(anchorDate);
      end = startOfYear(addYears(anchorDate, 1));
      break;
    case "agenda":
      // The agenda lists the anchor month on its own, so it needs no grid padding.
      start = startOfMonth(anchorDate);
      end = startOfMonth(addMonths(anchorDate, 1));
      break;
    default: {
      // Cover the padded cells too, or the events sitting on them never load.
      const { gridStart, gridEnd } = getMonthGridBounds(anchorDate);
      start = gridStart;
      end = startOfDay(addDays(gridEnd, 1));
      break;
    }
  }

  return { startAt: start.toISOString(), endAt: end.toISOString() };
}
